import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "mathematical-economics-optimization-lagrangian-notes",
  title: "Mathematical Economics Notes: Optimization, Lagrangian Multipliers & Constrained Maximization",
  description: "Complete study notes for MME I & II optimization — Unconstrained Optimization, Constrained Optimization, Bordered Hessian, Kuhn-Tucker conditions, and more.",
  category: "subject-explainers",
  authorSlug: "uday-kumar",
  pubDate: "2026-08-20",
  tags: ["mme", "optimization", "lagrangian", "mathematical-economics"],
  readMinutes: 13,
  heroEyebrow: "Subject Explainer · Mathematical Economics",
};

export const Body = () => {
  return (
    <article className="prose lg:prose-xl">
      <h1>{meta.title}</h1>
      <p>Optimization is the heart of mathematical economics. Whether maximizing utility or minimizing cost, mastering these techniques is essential for DU Economics students.</p>
      
      <h2>Unconstrained Optimization</h2>
      <p>For a function f(x, y), the First Order Condition (FOC) for a local extremum is that the first derivatives must equal zero: fx = 0 and fy = 0.</p>
      <p>The Second Order Condition (SOC) involves the Hessian matrix of second derivatives (fxx, fxy, fyx, fyy). For a maximum, fxx &lt; 0, fyy &lt; 0, and (fxx * fyy - fxy * fyx) &gt; 0.</p>

      <h2>Constrained Optimization: The Lagrangian Method</h2>
      <p>When maximizing f(x, y) subject to a constraint g(x, y) = c, we set up the Lagrangian: L = f(x, y) + lambda * (c - g(x, y)).</p>
      <p>FOCs: Lx = 0, Ly = 0, L_lambda = 0. The SOC is checked using the Bordered Hessian matrix.</p>

      <h2>Bordered Hessian</h2>
      <p>The Bordered Hessian incorporates the constraint's derivatives. For 2 variables and 1 constraint, a positive determinant of the 3x3 Bordered Hessian indicates a local maximum.</p>

      <h2>Kuhn-Tucker Conditions</h2>
      <p>For inequality constraints (g(x, y) &lt;= c), we use Kuhn-Tucker conditions. Complementary slackness requires lambda * (c - g(x, y)) = 0, with lambda &gt;= 0.</p>
      
      <h2>Envelope Theorem &amp; Comparative Statics</h2>
      <p>The Envelope Theorem states that the derivative of the value function with respect to a parameter equals the partial derivative of the Lagrangian with respect to that parameter, evaluated at the optimal point.</p>
      
      <h2>Homogeneous Functions &amp; Euler's Theorem</h2>
      <p>A function is homogeneous of degree r if f(tx, ty) = t^r * f(x, y). Euler's theorem states: x*fx + y*fy = r*f(x, y).</p>

      <h2>Worked Examples</h2>
      <h3>1. Utility Maximization</h3>
      <p>Maximize U(x, y) = x^0.5 * y^0.5 subject to Px*x + Py*y = M.</p>
      <p>L = x^0.5 * y^0.5 + lambda * (M - Px*x - Py*y). FOCs lead to x = M / (2*Px) and y = M / (2*Py).</p>
      
      <h3>2. Cost Minimization</h3>
      <p>Minimize w*L + r*K subject to q = L^alpha * K^beta. Yields standard factor demand equations.</p>
      
      <h3>3. Profit Maximization</h3>
      <p>Maximize Pi = p*f(K, L) - w*L - r*K. FOCs: p*fK = r and p*fL = w.</p>
    </article>
  );
};


